// Current day / week / month — the client's copy of src/task_periods.js.
//
// Labels must stay byte-identical to the server. The join key on Tasks is
// `period_label`; a second format splits one period in two.
//
// Owner, 2026-08-21: do not ask the server "what week are we in" on every
// sign-in. The week flips Monday 12:01am clinic time; the month on the 1st
// at 12:01am; the day every night at 12:01am. The clock is enough.

import { clinicToday } from './clinicDay';

function monthAbbr(monthIndex: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(2000, monthIndex, 2));
}

export const CADENCES = ['DAILY', 'WEEKLY', 'MONTHLY', 'MANAGER'] as const;
export type Cadence = (typeof CADENCES)[number];
export const DURATIONS = ['DAILY', 'WEEKLY', 'MONTHLY'] as const;
export type Duration = (typeof DURATIONS)[number];

export interface ComputedPeriod {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function asUtcMidnight(dateString: string): Date {
  return new Date(dateString);
}

function toDateString(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function dayPeriod(today: Date): ComputedPeriod {
  const s = toDateString(today);
  return {
    periodStart: s,
    periodEnd: s,
    periodLabel: `Day ${s} (${monthAbbr(today.getUTCMonth())} ${today.getUTCDate()})`,
  };
}

function weekPeriod(today: Date): ComputedPeriod {
  const day = today.getUTCDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);

  const thursdayPivot = new Date(monday);
  thursdayPivot.setUTCDate(monday.getUTCDate() + 3);
  const isoYear = thursdayPivot.getUTCFullYear();
  const yearStart = asUtcMidnight(`${isoYear}-01-01`);
  const isoWeek = Math.ceil(((thursdayPivot.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  const label = monday.getUTCMonth() === sunday.getUTCMonth()
    ? `Week ${isoYear}-W${pad2(isoWeek)} (${monthAbbr(monday.getUTCMonth())} ${monday.getUTCDate()}-${sunday.getUTCDate()})`
    : `Week ${isoYear}-W${pad2(isoWeek)} (${monthAbbr(monday.getUTCMonth())} ${monday.getUTCDate()} - ${monthAbbr(sunday.getUTCMonth())} ${sunday.getUTCDate()})`;

  return {
    periodStart: toDateString(monday),
    periodEnd: toDateString(sunday),
    periodLabel: label,
  };
}

function monthPeriod(today: Date): ComputedPeriod {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const first = asUtcMidnight(`${y}-${pad2(m + 1)}-01`);
  const lastDay = new Date(first);
  lastDay.setUTCMonth(m + 1, 0);
  return {
    periodStart: toDateString(first),
    periodEnd: toDateString(lastDay),
    periodLabel: `Month ${y}-${pad2(m + 1)} (${monthAbbr(m)} ${y})`,
  };
}

export function currentPeriod(cadence: Cadence, now: Date = new Date()): ComputedPeriod {
  const today = asUtcMidnight(clinicToday(now));
  switch (cadence) {
    case 'DAILY': return dayPeriod(today);
    case 'WEEKLY': return weekPeriod(today);
    case 'MONTHLY': return monthPeriod(today);
    // Same Mon–Sun window as WEEKLY so Submit still works until Calendar exists.
    case 'MANAGER': return weekPeriod(today);
    default: {
      const _x: never = cadence;
      throw new Error(`unknown cadence "${String(_x)}"; expected one of ${CADENCES.join(', ')}`);
    }
  }
}

export function currentPeriodSet(now: Date = new Date()): ComputedPeriod[] {
  return CADENCES.map((cadence) => currentPeriod(cadence, now));
}
